/**
 * useExportInvoicesOrders - Hook para exportar shipments elegibles a Excel
 *
 * Usa POST /api/v1/invoices/shipments/eligible/export
 * Body: { columns: string[] }
 * Query params: search, courier_ids, period, team_ids, shop_ids, etc. (igual que GET eligible)
 * Respuesta: { url_file: string } - URL de descarga del Excel
 *
 * @returns {Object} { exportEligibleShipments: función de exportación, isExporting: estado de carga }
 */

import { useState } from 'react';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { ShipmentsApiAdapter } from '../adapters/ShipmentsApiAdapter';
import { ShipmentsFilters } from '../adapters/ShipmentsApiAdapter';

// Columnas permitidas por el backend (allowedColumns)
const ALLOWED_EXPORT_COLUMNS = new Set([
  'id',
  'order_id',
  'ref',
  'shop_name',
  'team_name',
  'type',
  'courier_name',
  'weight_courier_kg',
  'weight_internal_kg',
  'country',
  'city',
  'zip',
  'num_items',
  'sequence',
  'shipping_date',
  'date_tracking_update',
  'last_status_at',
  'period',
  'financial_status',
  'shipment_status',
  'pre_invoice_code',
  'invoice_erp_number',
  'order_total',
]);

// Mapeo de columnas AG Grid -> columnas API para export
const FIELD_TO_EXPORT_COLUMN: Record<string, string> = {
  id: 'id',
  order_id: 'order_id',
  ref: 'ref',
  shop: 'shop_name',
  client: 'team_name',
  type: 'type',
  courier: 'courier_name',
  country: 'country',
  city: 'city',
  zip: 'zip',
  num_items: 'num_items',
  weight_internal_kg: 'weight_internal_kg',
  processed_at: 'shipping_date',
  date_tracking_update: 'date_tracking_update',
  last_status_at: 'last_status_at',
  period: 'period',
  financial_status: 'financial_status',
  logistic_status: 'shipment_status',
  pre_invoice_id: 'pre_invoice_code',
  invoice_id: 'invoice_erp_number',
  order_total: 'order_total',
};

const DEFAULT_EXPORT_COLUMNS = [
  'id',
  'ref',
  'shop_name',
  'team_name',
  'courier_name',
  'country',
  'shipping_date',
  'financial_status',
];

export const useExportInvoicesOrders = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('invoicesOrdersModule');

  const exportEligibleShipments = async (
    filters: ShipmentsFilters,
    selectedColumns?: string[]
  ) => {
    setIsExporting(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ShipmentsApiAdapter(httpClient);

      const columns =
        selectedColumns && selectedColumns.length > 0
          ? selectedColumns
              .map((field) => FIELD_TO_EXPORT_COLUMN[field] ?? field)
              .filter((col) => col && col !== 'actions' && ALLOWED_EXPORT_COLUMNS.has(col))
          : DEFAULT_EXPORT_COLUMNS;

      const response = await adapter.exportEligibleShipments(filters, columns);

      if (response.url_file) {
        window.open(response.url_file, '_blank');

        toast({
          title: t('export.success.title'),
          description: t('export.success.description'),
          variant: 'default',
        });
      } else {
        throw new Error('No se recibió URL del archivo');
      }
    } catch (error: any) {
      toast({
        title: t('export.error.title'),
        description: error.message || t('export.error.unexpected'),
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportEligibleShipments,
    isExporting,
  };
};
